class WhalebudHomeHeroSlider extends HTMLElement {
  connectedCallback() {
    if (this.dataset.whalebudInitialized === 'true') return;

    this.dataset.whalebudInitialized = 'true';
    this.slides = Array.from(this.querySelectorAll('[data-whalebud-hero-slide]'));
    this.previousButton = this.querySelector('[data-whalebud-hero-previous]');
    this.nextButton = this.querySelector('[data-whalebud-hero-next]');
    this.paginationButtons = Array.from(this.querySelectorAll('[data-whalebud-hero-pagination]'));
    this.currentIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('whalebud-home-hero-slider__slide--active')));
    this.autoplayDelay = Number(this.dataset.whalebudAutoplayDelay) || 25000;
    this.autoplayEnabled = this.dataset.whalebudAutoplay === 'true';
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.timer = null;
    this.drag = null;

    this.handlePrevious = () => this.select(this.currentIndex - 1, true);
    this.handleNext = () => this.select(this.currentIndex + 1, true);
    this.handlePaginationClick = (event) => {
      const index = Number(event.currentTarget.dataset.whalebudHeroIndex);
      if (Number.isInteger(index)) this.select(index, true);
    };
    this.handleKeydown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.handleNext();
      }
    };
    this.handlePointerEnter = () => this.stopAutoplay();
    this.handlePointerLeave = () => this.startAutoplay();
    this.handleFocusIn = () => this.stopAutoplay();
    this.handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    };
    this.handleVisibilityChange = () => {
      document.hidden ? this.stopAutoplay() : this.startAutoplay();
    };
    this.handleReducedMotion = () => {
      this.reducedMotion.matches ? this.stopAutoplay() : this.startAutoplay();
    };
    this.handlePointerDown = (event) => {
      if (this.slides.length < 2 || !event.isPrimary || event.button > 0) return;
      if (event.target.closest('a, button, input, select, textarea, label')) return;

      this.stopAutoplay();
      this.drag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        axis: null,
        previewSlide: null,
      };
      this.setPointerCapture(event.pointerId);
    };
    this.handlePointerMove = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const deltaX = event.clientX - this.drag.startX;
      const deltaY = event.clientY - this.drag.startY;

      if (!this.drag.axis) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < 6) return;
        this.drag.axis = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
      }
      if (this.drag.axis !== 'horizontal') return;

      event.preventDefault();
      this.drag.deltaX = deltaX;
      this.renderDrag(deltaX);
    };
    this.handlePointerEnd = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;

      const { axis, deltaX } = this.drag;
      const threshold = Math.min(120, Math.max(50, this.clientWidth * 0.1));
      this.resetDrag();

      if (axis === 'horizontal' && Math.abs(deltaX) >= threshold) {
        this.select(this.currentIndex + (deltaX < 0 ? 1 : -1), true);
      } else {
        this.startAutoplay();
      }
    };
    this.handlePointerCancel = (event) => {
      if (!this.drag || event.pointerId !== this.drag.pointerId) return;
      this.resetDrag();
      this.startAutoplay();
    };
    this.handleBlockSelect = (event) => {
      const selectedSlide = event.target.closest?.('[data-whalebud-hero-slide]');
      if (!selectedSlide || !this.contains(selectedSlide)) return;

      const index = this.slides.indexOf(selectedSlide);
      if (index >= 0) this.select(index, false);
      this.stopAutoplay();
    };
    this.handleBlockDeselect = (event) => {
      if (this.contains(event.target)) this.startAutoplay();
    };

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.paginationButtons.forEach((button) => button.addEventListener('click', this.handlePaginationClick));
    this.addEventListener('keydown', this.handleKeydown);
    this.addEventListener('mouseenter', this.handlePointerEnter);
    this.addEventListener('mouseleave', this.handlePointerLeave);
    this.addEventListener('focusin', this.handleFocusIn);
    this.addEventListener('focusout', this.handleFocusOut);
    this.addEventListener('pointerdown', this.handlePointerDown);
    this.addEventListener('pointermove', this.handlePointerMove);
    this.addEventListener('pointerup', this.handlePointerEnd);
    this.addEventListener('pointercancel', this.handlePointerCancel);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.handleBlockDeselect);
    this.reducedMotion.addEventListener('change', this.handleReducedMotion);

    this.select(this.currentIndex, false);
    this.startAutoplay();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.resetDrag();
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.paginationButtons?.forEach((button) => button.removeEventListener('click', this.handlePaginationClick));
    this.removeEventListener('keydown', this.handleKeydown);
    this.removeEventListener('mouseenter', this.handlePointerEnter);
    this.removeEventListener('mouseleave', this.handlePointerLeave);
    this.removeEventListener('focusin', this.handleFocusIn);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.removeEventListener('pointerdown', this.handlePointerDown);
    this.removeEventListener('pointermove', this.handlePointerMove);
    this.removeEventListener('pointerup', this.handlePointerEnd);
    this.removeEventListener('pointercancel', this.handlePointerCancel);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.handleBlockDeselect);
    this.reducedMotion?.removeEventListener('change', this.handleReducedMotion);
    delete this.dataset.whalebudInitialized;
  }

  renderDrag(deltaX) {
    const activeSlide = this.slides[this.currentIndex];
    const direction = deltaX < 0 ? 1 : -1;
    const previewIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
    const previewSlide = this.slides[previewIndex];
    const width = this.clientWidth;

    if (this.drag.previewSlide && this.drag.previewSlide !== previewSlide) {
      this.clearDragSlide(this.drag.previewSlide);
    }

    this.drag.previewSlide = previewSlide;
    this.classList.add('whalebud-home-hero-slider--dragging');
    activeSlide.style.transform = `translate3d(${deltaX}px, 0, 0)`;
    previewSlide.classList.add('whalebud-home-hero-slider__slide--drag-preview');
    previewSlide.style.transform = `translate3d(${deltaX + direction * width}px, 0, 0)`;
  }

  clearDragSlide(slide) {
    slide?.classList.remove('whalebud-home-hero-slider__slide--drag-preview');
    slide?.style.removeProperty('transform');
  }

  resetDrag() {
    if (!this.drag) return;

    const activeSlide = this.slides[this.currentIndex];
    activeSlide?.style.removeProperty('transform');
    this.clearDragSlide(this.drag.previewSlide);
    this.classList.remove('whalebud-home-hero-slider--dragging');

    if (this.hasPointerCapture?.(this.drag.pointerId)) {
      this.releasePointerCapture(this.drag.pointerId);
    }
    this.drag = null;
  }

  select(index, restartAutoplay) {
    if (this.slides.length === 0) return;

    const nextIndex = (index + this.slides.length) % this.slides.length;
    this.currentIndex = nextIndex;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === nextIndex;
      slide.classList.toggle('whalebud-home-hero-slider__slide--active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    this.paginationButtons.forEach((button, buttonIndex) => {
      const active = buttonIndex === nextIndex;
      button.classList.toggle('whalebud-home-hero-slider__bullet--active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
    });

    if (restartAutoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.slides.length < 2 || this.reducedMotion.matches || document.hidden) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;

    this.stopAutoplay();
    this.timer = window.setTimeout(() => {
      this.select(this.currentIndex + 1, false);
      this.startAutoplay();
    }, this.autoplayDelay);
  }

  stopAutoplay() {
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('whalebud-home-hero-slider')) {
  customElements.define('whalebud-home-hero-slider', WhalebudHomeHeroSlider);
}
